import { useCallback } from 'react';
import update from 'immutability-helper';

import { GqlCustomNavigationItem } from 'types/graphql';

type UseNavigationItemDragDropProps = {
  currentPath: string[];
  navigationItems: GqlCustomNavigationItem[];
  setCurrentPath: React.Dispatch<React.SetStateAction<string[]>>;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
  setNavigationItems: React.Dispatch<React.SetStateAction<GqlCustomNavigationItem[]>>;
};

type UseNavigationItemDragDropReturn = {
  handleMoveItem: (dragIndex: number, hoverIndex: number) => void;
  handleRemoveItem: (itemId: string) => void;
};

function getItemsAtPath(items: GqlCustomNavigationItem[], path: string[]): GqlCustomNavigationItem[] {
  if (path.length === 0) {
    return items;
  }

  const [firstId, ...restPath] = path;
  const section = items.find((item) => item.id === firstId);
  if (!section) {
    return [];
  }

  return getItemsAtPath(section.items, restPath);
}

function updateItemsAtPath(
  items: GqlCustomNavigationItem[],
  path: string[],
  updater: (currentItems: GqlCustomNavigationItem[]) => GqlCustomNavigationItem[]
): GqlCustomNavigationItem[] {
  if (path.length === 0) {
    return updater(items);
  }

  const [firstId, ...restPath] = path;
  return items.map((item) => {
    if (item.id === firstId) {
      return {
        ...item,
        items: updateItemsAtPath(item.items, restPath, updater),
      };
    }
    return item;
  });
}

function updatePositions(items: GqlCustomNavigationItem[]): GqlCustomNavigationItem[] {
  return items.map((item, index) => ({
    ...item,
    position: index,
    items: updatePositions(item.items),
  }));
}

function removeItemFromTree(items: GqlCustomNavigationItem[], itemId: string): GqlCustomNavigationItem[] {
  return items
    .filter((item) => item.id !== itemId)
    .map((item) => ({
      ...item,
      items: removeItemFromTree(item.items, itemId),
    }));
}

export function useNavigationItemDragDrop({
  currentPath,
  navigationItems,
  setCurrentPath,
  setIsDirty,
  setNavigationItems,
}: UseNavigationItemDragDropProps): UseNavigationItemDragDropReturn {
  const handleMoveItem = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const currentItems = getItemsAtPath(navigationItems, currentPath);
      const draggedItem = currentItems[dragIndex];

      const reorderedItems = update(currentItems, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, draggedItem],
        ],
      });

      const updatedItems = updateItemsAtPath(navigationItems, currentPath, () =>
        reorderedItems.map((item, index) => ({ ...item, position: index }))
      );

      setNavigationItems(updatePositions(updatedItems));
      setIsDirty(true);
    },
    [navigationItems, currentPath, setNavigationItems, setIsDirty]
  );

  const handleRemoveItem = useCallback(
    (itemId: string) => {
      const pathIndex = currentPath.indexOf(itemId);
      if (pathIndex >= 0) {
        setCurrentPath(currentPath.slice(0, pathIndex));
      }
      setNavigationItems((prevItems) => {
        const newItems = removeItemFromTree(prevItems, itemId);
        return updatePositions(newItems);
      });
      setIsDirty(true);
    },
    [currentPath, setCurrentPath, setNavigationItems, setIsDirty]
  );

  return {
    handleMoveItem,
    handleRemoveItem,
  };
}

export { getItemsAtPath, updateItemsAtPath };
